import React, { memo, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import getConfig from 'next/config';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _isEqual from 'lodash/isEqual';

import { usePageEvent } from '@bonnet/next/use-page-event';

import { useFeatures } from '@atc/features';
import { useDevice } from '@atc/react-device';

import { sendClick, sendImpressions } from 'reaxl-analytics';
import { ndsEligibleImpression, preQualCTAClick } from 'reaxl-analytics-handlers';
import { ConnectedNativeDealCalculator } from 'reaxl-native-deal-calculator';

import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import globalConfigsDuck from '@/ducks/globalConfigsDuck.js';
import inventoryDuck from '@/ducks/inventoryDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import saveDealXgFetcher from '@/fetchers/ExportToDealXgFetcher';

import usePreQualEligibility from '@/hooks/usePreQualEligibility';

import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

import LazyComponent from '@/components/LazyComponent';

const AutoFiPreQual = dynamic(() => import(/* webpackChunkName: "AutoFiPreQual" */ '@/components/vehicledetails/AutoFiPreQual'), { ssr: false });
const RoadsterDigitalRetailing = dynamic(() => import(/* webpackChunkName: "RoadsterDigitalRetailing" */ '@/components/vehicledetails/RoadsterDigitalRetailing'), { ssr: false });
const CarNowDigitalRetailing = dynamic(() => import(/* webpackChunkName: "CarNowDigitalRetailing" */ '@/components/vehicledetails/CarNowDigitalRetailing'), { ssr: false });

const PsxCtaContainer = dynamic(() => import(/* webpackChunkName: "PsxContainers" */ './psx/PsxCtaContainer'), { ssr: false });
const PsxSellYourCarBoxContainer = dynamic(() => import(/* webpackChunkName: "PsxContainers" */ './psx/PsxSellYourCarBoxContainer'), { ssr: false });

function ConnectedCalculatorContainer({
    enableCreditAppLink,
    lockForm,
    onEmailCTAClick,
    config,
}) {
    const dispatch = useDispatch();
    const { onPageEnter } = usePageEvent('connectedCalculator');
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const autoFiPreQualUrl = useSelector(globalConfigsDuck.selectors.getAutoFiPreQualUrl);
    const personalization = useSelector(PersonalizationEngineModule.duck.selectors.selectPersonalization);

    const device = useDevice();
    const { publicRuntimeConfig } = getConfig();

    const { enableThirdParty, isAutoFiCTAEnabled, isRoadsterEnabled, isCarNowEnabled, prequalMinimumBreakpoint } = usePreQualEligibility(listing);

    const {
        nds_calc: [enableNDSCalc],
    } = useFeatures([
        'nds_calc',
    ]);

    const {
        accelerate,
        esntial,
        ecommWebsite = {},
        id,
        paymentServices: { digitalRetailingType } = {},
        pricingDetail: { salePrice = '', msrp = '' } = {},
        owner: { id: ownerId, privateSeller } = {},
        vin,
    } = listing;

    const price = salePrice || msrp;
    const enableSACACreditApp = enableCreditAppLink && digitalRetailingType !== 'accelerate';
    const currentPageName = useSelector(currentPageNameDuck.selectors.getDuckState);
    const handleCreditAppCTAClick = useCallback((keys) => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys(keys));
    }, [dispatch]);

    const isPrequalMinimumBreakpoint = device?.greaterThan?.[prequalMinimumBreakpoint]
        || device?.is?.[prequalMinimumBreakpoint]
        || false;

    useEffect(() => {
        // Log out once per listing that we have invalid Accelerate configs
        if (accelerate && digitalRetailingType !== 'accelerate') {
            // eslint-disable-next-line no-console
            console.log(`Shopper Deal Page disabled for listing id ${id}: unsupported DR version configured`);
            if (window && window.newrelic) {
                window.newrelic.addPageAction('ThirdPartyError', {
                    id,
                    svoc: ownerId,
                    component: 'ConnectedCalculatorContainer',
                    message: 'Shopper Deal Page disabled. Unsupported DR version configured.',
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const autoFiPixallParams = {
        calculatorType: 'payment',
        eventSource: 'calculator',
        eventType: 'step',
        feature: 'Autofi',
        label: 'loan',
        vendor: 'Autofi',
    };

    const roadsterPixallParams = {
        dealerCode: 'atfordcpo',
        eventSource: 'dealership',
        eventType: 'click',
        feature: 'NDS Calculator',
        label: 'cnd_dl_onl - fyc_vdp-roadster',
        program: 'fba',
    };

    const carNowPixallParams = {
        label: 'cnd_dl_onl-fyc_vdp-car-now',
        eventSource: 'vehicle',
        par: 'v_lstg',
    };

    const ctaClassNames = clsx('margin-bottom-4');

    const onClickTracking = useCallback((epn, pixallData = {}) => {
        const data = {
            epn,
            pixallData,
            uiContext: 'simplified',
            inventoryId: id,
        };

        return (event) => sendClick(preQualCTAClick, event, data);
    }, [id]);

    // priority for customCTA to display in NDS: PSX > Roadster > AutoFi > CarNow
    let customCallToAction;

    if (isCarNowEnabled) {
        customCallToAction = (
            <CarNowDigitalRetailing
                className={ctaClassNames}
                vin={vin}
                onCarNowDRClick={onClickTracking('CarNow', carNowPixallParams)}
            />
        );
    }

    if (enableThirdParty && !!price && isPrequalMinimumBreakpoint && !privateSeller) {
        if (isAutoFiCTAEnabled) {
            customCallToAction = (
                <AutoFiPreQual
                    className={ctaClassNames}
                    onAutoFiPreQualClick={onClickTracking('Autofi', autoFiPixallParams)}
                    autoFiPreQualUrl={autoFiPreQualUrl}
                    listing={listing}
                />
            );
        }

        if (isRoadsterEnabled) {
            customCallToAction = (
                <RoadsterDigitalRetailing
                    className={ctaClassNames}
                    listing={listing}
                    onRoadsterDRClick={onClickTracking('Roadster', roadsterPixallParams)}
                    price={`${price}`}
                />
            );
        }
    }

    if (privateSeller) {
        customCallToAction = (
            <LazyComponent>
                <PsxCtaContainer
                    layout="makeOffer"
                    className="margin-bottom-4"
                />
            </LazyComponent>
        );
    }

    onPageEnter(() => {
        if (sendImpressions && id) {
            sendImpressions({ name: ndsEligibleImpression, data: { listingId: id } });
        }
    });

    const additionalContent = privateSeller && (
        <LazyComponent>
            <PsxSellYourCarBoxContainer />
        </LazyComponent>
    );

    return (
        <ConnectedNativeDealCalculator
            additionalContent={additionalContent}
            config={config}
            enableNDSCalc={enableNDSCalc}
            inventoryDuck={inventoryDuck}
            paymentsDuck={paymentsDuck}
            dealsDuck={dealsDuck}
            userDuck={userDuck}
            personalization={personalization}
            listingId={id}
            listingVin={vin}
            lockForm={lockForm}
            enableCreditAppLink={enableCreditAppLink}
            enableSACACreditApp={enableSACACreditApp}
            onEmailCTAClick={onEmailCTAClick}
            onCreditAppCTAClick={handleCreditAppCTAClick}
            pageName={currentPageName}
            customCallToAction={customCallToAction}
            enableEsntial={esntial && !esntial.deemphasized && Object.keys(ecommWebsite)?.length > 0}
            saveDealXgFetcher={saveDealXgFetcher}
            env={publicRuntimeConfig?.environment === 'prod' ? 'prod' : 'staging'}
        />
    );
}

export default memo(ConnectedCalculatorContainer, _isEqual);
